import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Skeleton,
  Text,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverHeader,
  Checkbox,
  CheckboxGroup,
  RadioGroup,
  Radio,
  Stack,
  Badge,
  Divider,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { SearchIcon, ChevronDownIcon, AddIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  usePlaylistsInfinite,
  useFavorites,
  useLibraryFilterOptions,
  useDeletePlaylist,
  useDuplicatePlaylist,
} from '../hooks/usePlaylists';
import { PlaylistCard } from '../components/PlaylistCard';
import { CreatePlaylistModal } from '../components/CreatePlaylistModal';
import { LibraryTabs } from '../components/LibraryTabs';
import type { PlaylistFilterParams } from '../types';

type SortKey = 'newest' | 'nameAsc' | 'nameDesc' | 'tracks';
type SourceFilter = 'all' | 'library' | 'own';

const SORT_TO_PARAMS: Record<SortKey, Pick<PlaylistFilterParams, 'sortBy' | 'sortOrder'>> = {
  newest: { sortBy: 'updatedAt', sortOrder: 'desc' },
  nameAsc: { sortBy: 'name', sortOrder: 'asc' },
  nameDesc: { sortBy: 'name', sortOrder: 'desc' },
  tracks: { sortBy: 'trackcount', sortOrder: 'desc' },
};

export function PlaylistsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const createModal = useDisclosure();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('newest');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [source, setSource] = useState<SourceFilter>('all');
  const [genres, setGenres] = useState<string[]>([]);
  const [moods, setMoods] = useState<string[]>([]);
  const [tempos, setTempos] = useState<string[]>([]);

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const params = useMemo<PlaylistFilterParams>(
    () => ({ search: search || undefined, genres, moods, tempos, ...SORT_TO_PARAMS[sort] }),
    [search, genres, moods, tempos, sort],
  );

  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    usePlaylistsInfinite(params);
  const { favoriteIds, toggleFavorite } = useFavorites();
  const { data: categories } = useLibraryFilterOptions();
  const deletePlaylist = useDeletePlaylist();
  const duplicatePlaylist = useDuplicatePlaylist();

  const items = useMemo(() => {
    const all = data?.pages.flatMap((p) => p.items) ?? [];
    return all
      .filter((p) =>
        source === 'all' ? true : source === 'library' ? p.source === 'library' : p.source === 'custom',
      )
      .filter((p) => !favoritesOnly || favoriteIds.has(p.id));
  }, [data, source, favoritesOnly, favoriteIds]);

  const activeFilterCount =
    (favoritesOnly ? 1 : 0) + (source !== 'all' ? 1 : 0) + genres.length + moods.length + tempos.length;

  const handleDuplicate = (id: string) => {
    duplicatePlaylist.mutate(id, {
      onSuccess: () => toast({ status: 'success', title: t('playlists.duplicated'), duration: 2000 }),
      onError: () => toast({ status: 'error', title: t('common.error', 'Something went wrong') }),
    });
  };

  const handleDelete = (id: string, name: string) => {
    if (!window.confirm(t('playlists.deleteConfirmNamed', { name }))) return;
    deletePlaylist.mutate(id, {
      onSuccess: () => toast({ status: 'success', title: t('playlists.deleted'), duration: 2000 }),
      onError: () => toast({ status: 'error', title: t('common.error', 'Something went wrong') }),
    });
  };

  const clearFilters = () => {
    setFavoritesOnly(false);
    setSource('all');
    setGenres([]);
    setMoods([]);
    setTempos([]);
  };

  return (
    <Box>
      <LibraryTabs />
      <Flex justify="space-between" align="center" mb={4} gap={3} wrap="wrap">
        <Heading size="lg" color="gray.800">
          {t('playlists.title')}
        </Heading>
        <Button colorScheme="brand" leftIcon={<AddIcon />} onClick={createModal.onOpen}>
          {t('playlists.create')}
        </Button>
      </Flex>

      <HStack mb={5} spacing={3} wrap="wrap">
        <InputGroup maxW="320px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder={t('playlists.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </InputGroup>

        <Menu>
          <MenuButton as={Button} variant="outline" rightIcon={<ChevronDownIcon />}>
            {t(`playlists.sort.${sort}`)}
          </MenuButton>
          <MenuList>
            <MenuOptionGroup
              type="radio"
              value={sort}
              onChange={(v) => setSort(v as SortKey)}
            >
              <MenuItemOption value="newest">{t('playlists.sort.newest')}</MenuItemOption>
              <MenuItemOption value="nameAsc">{t('playlists.sort.nameAsc')}</MenuItemOption>
              <MenuItemOption value="nameDesc">{t('playlists.sort.nameDesc')}</MenuItemOption>
              <MenuItemOption value="tracks">{t('playlists.sort.tracks')}</MenuItemOption>
            </MenuOptionGroup>
          </MenuList>
        </Menu>

        <Popover placement="bottom-start">
          <PopoverTrigger>
            <Button variant="outline" rightIcon={<ChevronDownIcon />}>
              {t('playlists.filters')}
              {activeFilterCount > 0 && (
                <Badge ml={2} colorScheme="brand" borderRadius="full">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <PopoverHeader fontWeight="600">
              <Flex justify="space-between" align="center">
                {t('playlists.filters')}
                {activeFilterCount > 0 && (
                  <Button size="xs" variant="link" onClick={clearFilters}>
                    {t('playlists.clearFilters')}
                  </Button>
                )}
              </Flex>
            </PopoverHeader>
            <PopoverBody maxH="60vh" overflowY="auto">
              <Stack spacing={4}>
                <Checkbox
                  isChecked={favoritesOnly}
                  onChange={(e) => setFavoritesOnly(e.target.checked)}
                  colorScheme="brand"
                >
                  {t('playlists.favoritesOnly')}
                </Checkbox>

                <Box>
                  <Text fontSize="sm" fontWeight="600" mb={1}>
                    {t('playlists.source')}
                  </Text>
                  <RadioGroup value={source} onChange={(v) => setSource(v as SourceFilter)}>
                    <Stack spacing={1}>
                      <Radio value="all" colorScheme="brand">{t('playlists.sourceAll')}</Radio>
                      <Radio value="library" colorScheme="brand">{t('playlists.sourceLibrary')}</Radio>
                      <Radio value="own" colorScheme="brand">{t('playlists.sourceOwn')}</Radio>
                    </Stack>
                  </RadioGroup>
                </Box>

                {!!categories?.genres.length && (
                  <FilterGroup
                    label={t('playlists.genre')}
                    options={categories.genres}
                    value={genres}
                    onChange={setGenres}
                  />
                )}
                {!!categories?.moods.length && (
                  <FilterGroup
                    label={t('playlists.mood')}
                    options={categories.moods}
                    value={moods}
                    onChange={setMoods}
                  />
                )}
                {!!categories?.tempos.length && (
                  <FilterGroup
                    label={t('playlists.tempo')}
                    options={categories.tempos}
                    value={tempos}
                    onChange={setTempos}
                  />
                )}
              </Stack>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </HStack>

      {isError ? (
        <EmptyState title={t('common.error', 'Something went wrong')} />
      ) : isLoading ? (
        <SimpleGrid columns={{ base: 2, md: 3, lg: 4, xl: 5 }} spacing={4}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} borderRadius="lg" height="240px" />
          ))}
        </SimpleGrid>
      ) : items.length === 0 ? (
        <EmptyState title={t('playlists.empty')} description={t('playlists.emptyDescription')} />
      ) : (
        <>
          <SimpleGrid columns={{ base: 2, md: 3, lg: 4, xl: 5 }} spacing={4}>
            {items.map((p) => (
              <PlaylistCard
                key={p.id}
                playlist={p}
                isFavorite={favoriteIds.has(p.id)}
                onToggleFavorite={() => toggleFavorite(p.id)}
                onOpen={() => navigate(`/library/playlists/${p.id}`)}
                onDuplicate={() => handleDuplicate(p.id)}
                onDelete={() => handleDelete(p.id, p.name)}
              />
            ))}
          </SimpleGrid>

          {hasNextPage && (
            <Flex justify="center" mt={6}>
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                isLoading={isFetchingNextPage}
              >
                {t('playlists.loadMore')}
              </Button>
            </Flex>
          )}
        </>
      )}

      <CreatePlaylistModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        onCreated={(p) => {
          createModal.onClose();
          navigate(`/library/playlists/${p.id}`);
        }}
      />
    </Box>
  );
}

function FilterGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <Box>
      <Divider mb={2} />
      <Text fontSize="sm" fontWeight="600" mb={1}>
        {label}
      </Text>
      <CheckboxGroup value={value} onChange={(v) => onChange(v as string[])}>
        <Stack spacing={1}>
          {options.map((opt) => (
            <Checkbox key={opt} value={opt} colorScheme="brand" textTransform="capitalize">
              {opt}
            </Checkbox>
          ))}
        </Stack>
      </CheckboxGroup>
    </Box>
  );
}

function EmptyState({ title, description }: { title: string; description?: string }) {
  return (
    <Flex direction="column" align="center" justify="center" py={20} textAlign="center">
      <Text fontSize="lg" fontWeight="600" color="gray.600">
        {title}
      </Text>
      {description && (
        <Text fontSize="sm" color="gray.400" mt={1}>
          {description}
        </Text>
      )}
    </Flex>
  );
}

export default PlaylistsPage;
